import type { JailbreakTestResult } from '../../services/types';

interface TestResultDisplayProps {
  result: JailbreakTestResult;
}

export function TestResultDisplay({ result }: TestResultDisplayProps) {
  return (
    <div className="card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="heading-card text-neutral-900 dark:text-neutral-50">
          測試結果
        </h3>
        <span
          className={`badge ${result.success ? 'badge-success' : 'badge-danger'}`}
        >
          {result.success ? '成功' : '失敗'}
        </span>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span className="text-neutral-500 dark:text-neutral-400">模型:</span>
          <p className="font-medium text-neutral-900 dark:text-neutral-50">
            {result.model}
          </p>
        </div>
        <div>
          <span className="text-neutral-500 dark:text-neutral-400">延遲:</span>
          <p className="font-medium text-neutral-900 dark:text-neutral-50">
            {result.latency_ms}ms
          </p>
        </div>
        <div>
          <span className="text-neutral-500 dark:text-neutral-400">輸入 Token:</span>
          <p className="font-medium text-neutral-900 dark:text-neutral-50">
            {result.usage.input_tokens}
          </p>
        </div>
        <div>
          <span className="text-neutral-500 dark:text-neutral-400">輸出 Token:</span>
          <p className="font-medium text-neutral-900 dark:text-neutral-50">
            {result.usage.output_tokens}
          </p>
        </div>
      </div>

      {/* Cache Info */}
      {(result.usage.cache_creation_tokens !== undefined ||
        result.usage.cache_read_tokens !== undefined) && (
        <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-button">
          <p className="text-sm text-primary-700 dark:text-primary-300">
            <strong>Prompt Caching:</strong>
            {result.usage.cache_creation_tokens !== undefined &&
            result.usage.cache_creation_tokens > 0
              ? ` 寫入 ${result.usage.cache_creation_tokens} tokens`
              : ''}
            {result.usage.cache_read_tokens !== undefined &&
            result.usage.cache_read_tokens > 0
              ? ` 讀取 ${result.usage.cache_read_tokens} tokens`
              : ''}
          </p>
        </div>
      )}

      {/* Response Content */}
      <div>
        <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
          模型回應
        </h4>
        <pre className="p-4 bg-neutral-100 dark:bg-neutral-700 rounded text-sm font-mono text-neutral-700 dark:text-neutral-200 overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap">
          {result.content}
        </pre>
      </div>

      {/* Raw Request */}
      {result.raw_request && (
        <details className="border border-neutral-200 dark:border-neutral-700 rounded-button">
          <summary className="p-3 cursor-pointer text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
            Raw Request (API 請求參數)
          </summary>
          <pre className="p-4 bg-neutral-100 dark:bg-neutral-800 text-xs font-mono text-neutral-600 dark:text-neutral-300 overflow-x-auto max-h-96 overflow-y-auto">
            {JSON.stringify(result.raw_request, null, 2)}
          </pre>
        </details>
      )}

      {/* Raw Response */}
      {result.raw_response !== undefined && result.raw_response !== null && (
        <details className="border border-neutral-200 dark:border-neutral-700 rounded-button">
          <summary className="p-3 cursor-pointer text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
            Raw Response
          </summary>
          <pre className="p-4 bg-neutral-100 dark:bg-neutral-800 text-xs font-mono text-neutral-600 dark:text-neutral-300 overflow-x-auto max-h-96 overflow-y-auto">
            {JSON.stringify(result.raw_response, null, 2)}
          </pre>
        </details>
      )}

      {/* Debug Events */}
      {result.debug && (
        <details className="border border-primary-200 dark:border-primary-800/50 rounded-button bg-primary-50 dark:bg-primary-900/20">
          <summary className="p-3 cursor-pointer text-sm font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/30">
            Debug Events ({result.debug.summary.totalEvents} 事件，耗時 {result.debug.summary.totalDurationMs}ms)
          </summary>
          <div className="p-4 space-y-3">
            {/* Event Summary */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(result.debug.summary.eventCounts).map(([eventType, count]) => (
                <span
                  key={eventType}
                  className="px-2 py-1 text-xs font-mono bg-primary-100 dark:bg-primary-800/50 text-primary-700 dark:text-primary-300 rounded"
                >
                  {eventType}: {count}
                </span>
              ))}
            </div>
            {/* Event Timeline */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {result.debug.events.map((event, idx) => {
                const relativeTime = event.timestamp - result.debug!.summary.firstEventTimestamp;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-2 text-xs font-mono border-l-2 border-primary-300 dark:border-primary-600 pl-2"
                  >
                    <span className="text-primary-500 dark:text-primary-400 shrink-0">
                      +{relativeTime}ms
                    </span>
                    <span className="font-semibold text-primary-700 dark:text-primary-300">
                      {event.type}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Full Events JSON */}
            <details className="border border-primary-200 dark:border-primary-700 rounded">
              <summary className="p-2 cursor-pointer text-xs text-primary-600 dark:text-primary-400">
                展開完整事件 JSON
              </summary>
              <pre className="p-2 text-xs font-mono text-neutral-600 dark:text-neutral-300 overflow-x-auto max-h-64 overflow-y-auto">
                {JSON.stringify(result.debug.events, null, 2)}
              </pre>
            </details>
          </div>
        </details>
      )}
    </div>
  );
}
